import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router';
import '@fontsource-variable/inter';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
// Arabic: Changa (technical display face) + Cairo (body/UI). Unicode-range subsets: only used glyph ranges download.
import '@fontsource-variable/changa';
import '@fontsource-variable/cairo';
import '@fontsource-variable/heebo';
import './styles/index.css';
import { applyLanguage, detectLanguage, ensureLocale, isLang } from './i18n';
import { router } from './app/router';
import { ApiError } from './services/api';
import { MOBILE_LAYOUT_QUERY, PREVIEW_SEQUENCE, heroRenderWidth, pickSequence } from './animations/ScrollSequence/sequenceConfig';
import { frameUrl } from './animations/ScrollSequence/frameLoader';

// Homepage: start fetching the hero immediately — the preview's first frame and first bundle
// (tiny, on screen first) and the first frame of the set HeroSequence scrolls with — preloaded
// as fetches the frame loaders reuse.
if (/^\/(en|ar|he)?\/?$/.test(window.location.pathname)) {
  const mobileLayout = window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
  const { motion } = pickSequence(heroRenderWidth(mobileLayout, window.innerWidth, window.innerHeight), window.devicePixelRatio);
  const urls = [
    frameUrl(PREVIEW_SEQUENCE.baseUrl, PREVIEW_SEQUENCE.prefix, PREVIEW_SEQUENCE.extension, PREVIEW_SEQUENCE.padLength, 0),
    frameUrl(motion.baseUrl, motion.prefix, motion.extension, motion.padLength, 0),
    ...(PREVIEW_SEQUENCE.bundles?.[0] ? [PREVIEW_SEQUENCE.bundles[0].url] : []),
  ];
  for (const href of urls) {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'fetch';
    link.crossOrigin = 'anonymous';
    link.href = href;
    link.setAttribute('fetchpriority', 'high');
    document.head.appendChild(link);
  }
}

// Apply the language from the URL before the first paint (correct lang/dir, no RTL flash).
const firstSegment = window.location.pathname.split('/')[1];
const initialLang = isLang(firstSegment) ? firstSegment : detectLanguage();
await ensureLocale(initialLang);
applyLanguage(initialLang);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, error) => !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2,
    },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
